<template>
  <div class="p-3">
    <!-- Info Panel -->
    <div class="bg-blue-50 mb-5 animate-fade-in">
      <div class="flex items-start">
        <h4>Submit to Competition</h4>
        <div class="p-3 competition-info">
          <p class="text-sm text-blue-800 mb-1" style="font-weight:500; color:dodgerblue;">
            <md-icon class="text-primary mr-1" style="font-size: 18px">info</md-icon> About Skills Competitions
          </p>
          <div class="ml-4 text-blue-700">
            <p class="mb-2" style="color:cornflowerblue;">
              Skills competitions allow you to showcase your healthcare abilities against other students. 
              Your skill card will be evaluated and ranked on a leaderboard with participants from other schools.
            </p>
            <p class="mb-0" style="color:cornflowerblue;">
              You can choose to submit to an available competition or skip this step to create a regular skill card for practice.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading competitions...</span>
      </div>
      <p class="mt-2 text-muted">Checking for available competitions...</p>
    </div>

    <!-- No Competitions Available -->
    <div v-else-if="!loading && eligibleCompetitions.length === 0" class="alert alert-warning">
      <div class="d-flex align-items-center">
        <i class="material-icons mr-2">warning</i>
        <div>
          <strong>No competitions available</strong>
          <p class="mb-0">Sorry, there are no competitions available that you can submit to at this time.</p>
        </div>
      </div>
    </div>

    <!-- Competition Selection -->
    <div v-else class="competition-section">
      <div class="section-header mb-3">
        <h5 class="text-lg font-medium text-gray-900">Available Competitions</h5>
      </div>
      
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <b-form-group label="Select Competition" label-class="form-label">
          <v-select
            class="form-control v-select-custom"
            placeholder="Choose a competition..."
            v-model="selectedCompetition"
            :options="competitionOptions"
            :reduce="opt => opt.value"
            :loading="loading"
            :clearable="true"
            @input="onCompetitionChanged"
          >
            <template #no-options>
              No competitions available
            </template>
          </v-select>
        </b-form-group>

        <!-- Selected Competition Details -->
        <div v-if="selectedCompetitionDetails" class="card mt-3">
          <div class="card-body">
            <h5 class="card-title">{{ selectedCompetitionDetails.title }}</h5>
            <p class="card-text">{{ selectedCompetitionDetails.description }}</p>
            
            <div class="row">
              <div class="col-md-6">
                <small>
                  <strong class="text-black">Start Date:</strong> <span class="text-muted">{{ formatDate(selectedCompetitionDetails.competition_start_date) }}</span>
                </small>
              </div>
              <div class="col-md-6">
                <small>
                  <strong class="text-black">End Date:</strong> <span class="text-muted">{{ formatDate(selectedCompetitionDetails.competition_end_date) }}</span>
                </small>
              </div>
            </div>
            
            <div class="mt-2">
              <span class="badge badge-success" style="text-transform: none;">Schools participants ({{ selectedCompetitionDetails.participating_schools_count }})</span>
            </div>
          </div>
        </div>

        <!-- Optional: Skip Competition -->
        <div class="mt-3">
          <div class="form-check">
            <input
              id="skip-competition"
              v-model="skipCompetition"
              class="form-check-input"
              type="checkbox"
              @change="onSkipCompetitionChanged"
            >
            <label class="form-check-label" for="skip-competition">
              Skip competition submission (submit as regular skill card)
            </label>
          </div>
        </div>
      </div>
    </div>

    <!-- Navigation Buttons -->
    <div class="d-flex justify-content-between mt-4">
      <b-btn class="border" variant="ghost" @click="goBack">
        BACK
      </b-btn>
      <b-button 
        variant="primary" 
        @click="continueToNext"
      >
        Next: Anchor Elements
        <i class="material-icons ml-1">navigate_next</i>
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

export default {
  name: 'CompetitionSelectionForm',
  props: {
    myIndex: {
      type: Number,
      default: 1
    },
    onNext: {
      type: Function,
      default: () => {}
    },
    activeTabIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: false,
      eligibleCompetitions: [],
      selectedCompetition: '',
      selectedCompetitionDetails: null,
      skipCompetition: false
    };
  },
  computed: {
    ...mapState('aiSkills', ['skillCard']),
    
    canProceed() {
      return this.selectedCompetition || this.skipCompetition || this.eligibleCompetitions.length === 0;
    },

    competitionOptions() {
      return this.eligibleCompetitions.map(competition => ({
        label: competition.title,
        value: competition.id
      }));
    }
  },
  watch: {
    activeTabIndex(newVal) {
      if (newVal === this.myIndex) {
        this.loadEligibleCompetitions();
      }
    }
  },
  mounted() {
    if (this.activeTabIndex === this.myIndex) {
      this.loadEligibleCompetitions();
    }
  },
  methods: {
    ...mapActions('skillsCompetitions', [
      'getEligibleCompetitionsForStudent',
      'getAllCompetitions'
    ]),
    ...mapActions('aiSkills', ['setSkillCardValues']),

    async loadEligibleCompetitions() {
      this.loading = true;
      try {
        // Get eligible competitions for the student
        const response = await this.getEligibleCompetitionsForStudent();
        this.eligibleCompetitions = response.data || [];
        
        // If no eligible competitions from the specific endpoint, try getting all active competitions
        if (this.eligibleCompetitions.length === 0) {
          const allCompetitionsResponse = await this.getAllCompetitions({
            is_active: true,
            is_published: true
          });
          
          // Filter competitions that are within date range
          const now = new Date();
          this.eligibleCompetitions = (allCompetitionsResponse.data || []).filter(competition => {
            const startDate = new Date(competition.competition_start_date);
            const endDate = new Date(competition.competition_end_date);
            return now >= startDate && now <= endDate;
          });
        }
        
        // Restore previously selected competition if any
        if (this.skillCard.competition) {
          this.selectedCompetition = this.skillCard.competition;
          this.updateCompetitionDetails();
        }
        
      } catch (error) {
        // Handle error silently or show user-friendly message
        this.eligibleCompetitions = [];
      } finally {
        this.loading = false;
      }
    },

    onCompetitionChanged() {
      this.skipCompetition = false;
      this.updateCompetitionDetails();
      this.updateSkillCard();
    },

    onSkipCompetitionChanged() {
      if (this.skipCompetition) {
        this.selectedCompetition = '';
        this.selectedCompetitionDetails = null;
      }
      this.updateSkillCard();
    },

    updateCompetitionDetails() {
      if (this.selectedCompetition) {
        this.selectedCompetitionDetails = this.eligibleCompetitions.find(
          comp => comp.id == this.selectedCompetition
        );
      } else {
        this.selectedCompetitionDetails = null;
      }
    },

    updateSkillCard() {
      // Update the skill card with selected competition
      const updatedSkillCard = { ...this.skillCard };
      updatedSkillCard.competition = this.selectedCompetition || null;
      this.setSkillCardValues(updatedSkillCard);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    goBack() {
      this.onNext(0);
    },

    continueToNext() {
      this.onNext(2);
    }
  }
};
</script>

<style scoped>
.competition-info {
  background-color: rgb(239 246 255 / var(--tw-bg-opacity, 1));
  border-radius: 8px;
  font-size: medium;
}

.competition-section {
  margin-bottom: 2rem;
}

.section-header h5 {
  margin: 0;
  color: #374151;
  font-weight: 600;
}

.form-label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.v-select-custom {
  border-radius: 0.375rem;
}

.bg-white.rounded-lg {
  transition: all 0.2s ease-in-out;
}

.bg-white.rounded-lg:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.card {
  border: 1px solid #dee2e6;
}

.badge {
  font-size: 0.875rem;
}

.alert {
  border-radius: 0.375rem;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}
</style>
